import { useMemo, useState } from "react";
import { Box, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Stack, IconButton, Collapse } from "@mui/material";
import { Check, Close, KeyboardArrowDown } from '@mui/icons-material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

interface ViewVisitProps {
    data: any[];
    details?: any;
}
const ViewCostumer = ({ data, details }: ViewVisitProps) => {
    const rowsPerPage = 15;
    const [page, setPage] = useState<number>(1);
    const [effectiveSaleFilter, setEffectiveSaleFilter] = useState<'all' | 'yes' | 'no'>('all');
    const [showFilters, setShowFilters] = useState<boolean>(false);

    // Filtrar datos por venta efectiva
    const filteredData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        if (effectiveSaleFilter === 'all') return data;

        return data.filter(visit => {
            const status = visit.canceled ? "canceled" : visit.saveOrderSap ? "saved" : "pending";
            const isEffective = status === "saved"; // Venta efectiva = guardada
            return effectiveSaleFilter === 'yes' ? isEffective : !isEffective;
        });
    }, [data, effectiveSaleFilter]);

    const pagedData = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredData.slice(start, start + rowsPerPage);
    }, [filteredData, page, rowsPerPage]);


    const pageCount = useMemo(() => {
        if (filteredData.length === 0) return 1;
        return Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    }, [filteredData, rowsPerPage]);

    // Manejar cambio en filtro de venta efectiva
    const handleEffectiveSaleFilterChange = (value: 'all' | 'yes' | 'no') => {
        setEffectiveSaleFilter(value);
        setPage(1);
    };

    return (
        <>
            <Box sx={{ width: "100%", px: 2, mb: 2, pt: 3 }}>
                <Paper elevation={2} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterAltIcon sx={{ mr: 1, color: '#183555' }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#183555' }}>
                                Filtros
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {effectiveSaleFilter !== 'all' && (
                                <Typography variant="body2" sx={{ ml: 'auto', color: '#666' }}>
                                    {filteredData.length} de {data?.length || 0} clientes
                                </Typography>
                            )}
                            <IconButton onClick={() => setShowFilters(prev => !prev)}>
                                <KeyboardArrowDown sx={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
                            </IconButton>
                        </Box>
                    </Box>
                    <Collapse in={showFilters} timeout="auto" unmountOnExit>
                        <Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#183555', mb: 1 }}>
                                Visita efectiva:
                            </Typography>
                            <Stack direction="row" spacing={1.5}>
                                <Chip
                                    icon={<Check />}
                                    label="Sí"
                                    onClick={() => handleEffectiveSaleFilterChange(effectiveSaleFilter === 'yes' ? 'all' : 'yes')}
                                    color="success"
                                    variant={effectiveSaleFilter === 'yes' ? "filled" : "outlined"}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2,
                                        }
                                    }}
                                />
                                <Chip
                                    icon={<Close />}
                                    label="No"
                                    onClick={() => handleEffectiveSaleFilterChange(effectiveSaleFilter === 'no' ? 'all' : 'no')}
                                    color="error"
                                    variant={effectiveSaleFilter === 'no' ? "filled" : "outlined"}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        transition: 'all 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 2,
                                        }
                                    }}
                                />
                            </Stack>

                            {effectiveSaleFilter !== 'all' && (
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Chip
                                        label="Limpiar filtro"
                                        onClick={() => handleEffectiveSaleFilterChange('all')}
                                        onDelete={() => handleEffectiveSaleFilterChange('all')}
                                        color="primary"
                                        variant="filled"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '0.875rem',
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Collapse>

                </Paper>
            </Box>
            <Box sx={{ width: "100%", p: 2, height: "calc(100% - 1000px)" }}>
                <Paper elevation={2}>
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Ruta</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Vendedor</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Codigo del cliente</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Razon social</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Nombre comercial</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Venta efectiva</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    pagedData.map((visit, idx) => {
                                        const status = visit.canceled ? "canceled" : visit.saveOrderSap ? "saved" : "pending";
                                        return (
                                            <TableRow key={visit.$key ?? `${page}-${idx}`}>
                                                <TableCell>
                                                    <Typography>{visit?.userRouter || "-"}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography>{visit?.vendorName || "-"}</Typography>
                                                </TableCell>
                                                <TableCell><Typography>{visit?.cardCode || "-"}</Typography></TableCell>
                                                <TableCell><Typography>{visit?.cardName || "-"}</Typography></TableCell>
                                                <TableCell><Typography>{visit?.cardFName || "-"}</Typography></TableCell>
                                                <TableCell>
                                                    {
                                                        status !== "canceled" ?
                                                            <>
                                                                {status === "saved" && <Typography sx={{ color: "#4caf50", fontWeight: 600 }}>Guardada</Typography>}
                                                                {status === "pending" && <Typography sx={{ color: "#00aeff", fontWeight: 600 }}>Pendiente</Typography>}
                                                            </>
                                                            :
                                                            <>
                                                                {
                                                                    visit.products.length > 0
                                                                        ? <Typography sx={{ color: "#f44336", fontWeight: 600 }}>Cancelada</Typography>
                                                                        : <Typography sx={{ color: "#f44336", fontWeight: 600 }}>Orden de no compra</Typography>
                                                                }
                                                            </>
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                }

                                {(filteredData.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={11} sx={{ py: 6, textAlign: "center" }}>
                                            <Typography color="text.secondary">
                                                {effectiveSaleFilter !== 'all'
                                                    ? "No hay clientes que coincidan con el filtro seleccionado."
                                                    : "No hay registros para mostrar."}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                        <Pagination
                            count={pageCount}
                            page={page}
                            onChange={(_, val) => setPage(val)}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                </Paper>
            </Box>
        </>
    );
}

export default ViewCostumer;